import { format } from 'date-fns';
import { GastoPlan, PagoPlan, Plan, Transaccion } from '../types';
import { aCentimos, aEuros } from './amediasUtils';

/**
 * Qué movimientos de Finanzas corresponden a un plan de A medias, y qué hay
 * que crear, cambiar o borrar para que cuadren. Funciones puras: quien las usa
 * decide cuándo aplicar el resultado.
 *
 * Por cada gasto del plan, para ti (participante `yoId`):
 *   - tu parte, si participas → gasto real en tu categoría          (rol 'parte')
 *   - lo pagado menos tu parte → adelanto con el plan como persona  (rol 'neto')
 *       > 0: gasto adelanto (pagaste por otros)
 *       < 0: ingreso adelanto (otro pagó lo tuyo; lo debes al plan)
 * Por cada pago en el que estés:
 *   - lo pagas tú   → gasto adelanto   (cierra lo que debías)
 *   - lo cobras tú  → ingreso adelanto (te devuelven lo adelantado)
 *
 * Así el saldo de la cuenta coincide siempre con el banco, tu gasto real cae
 * en la fecha del gasto y el Dashboard muestra lo pendiente con el plan.
 *
 * El plan manda en tipo, importe, fecha, descripción y persona. La cuenta y la
 * categoría solo se ponen al crear: si las cambias en Finanzas, se respetan.
 */

export interface MovimientoEsperado {
  /** Identifica de dónde sale: 'gasto:<id>:parte', 'gasto:<id>:neto', 'pago:<id>'. */
  clave: string;
  /** uid de quien apuntó el gasto o pago en el plan. */
  creadoPor: string;
  tipo: 'ingreso' | 'gasto';
  cantidad: number;
  fecha: Date;
  descripcion: string;
  adelanto: boolean;
  /** Solo en adelantos: el nombre del plan. */
  persona?: string;
}

export function movimientosEsperados(
  plan: Pick<Plan, 'nombre' | 'participantes'>, yoId: string, gastos: GastoPlan[], pagos: PagoPlan[],
): MovimientoEsperado[] {
  const out: MovimientoEsperado[] = [];
  const persona = plan.nombre.trim();
  const nombre = (id: string) => plan.participantes.find(p => p.id === id)?.nombre ?? 'otro participante';

  gastos.forEach(g => {
    const pagado = aCentimos(g.pagadoPor[yoId] ?? 0);
    const parte = aCentimos(g.importes[yoId] ?? 0);
    const comun = { creadoPor: g.creadoPorUid, fecha: g.fecha };

    if (parte > 0) {
      out.push({
        ...comun, clave: `gasto:${g.id}:parte`, tipo: 'gasto', cantidad: aEuros(parte),
        descripcion: `${g.concepto} · ${persona}`, adelanto: false,
      });
    }
    const neto = pagado - parte;
    if (neto !== 0) {
      out.push({
        ...comun, clave: `gasto:${g.id}:neto`,
        tipo: neto > 0 ? 'gasto' : 'ingreso',
        cantidad: aEuros(Math.abs(neto)),
        descripcion: neto > 0
          ? `${g.concepto} · lo que adelantas a los demás`
          : `${g.concepto} · tu parte, que pagó ${Object.keys(g.pagadoPor).map(nombre).join(', ')}`,
        adelanto: true, persona,
      });
    }
  });

  pagos.forEach(p => {
    if (p.de !== yoId && p.a !== yoId) return;
    const pagas = p.de === yoId;
    out.push({
      creadoPor: p.creadoPorUid, fecha: p.fecha, clave: `pago:${p.id}`,
      tipo: pagas ? 'gasto' : 'ingreso',
      cantidad: p.importe,
      descripcion: pagas ? `Pago a ${nombre(p.a)}` : `Cobro de ${nombre(p.de)}`,
      adelanto: true, persona,
    });
  });

  return out;
}

export interface CambioTransaccion {
  transaccion: Transaccion;
  esperado: MovimientoEsperado;
}

export interface DiferenciasSincronizacion {
  crear: MovimientoEsperado[];
  actualizar: CambioTransaccion[];
  /** Ya no corresponden a nada del plan (gasto borrado, ya no participas…). */
  borrar: Transaccion[];
}

const mismoDia = (a: Date, b: Date) => format(a, 'yyyy-MM-dd') === format(b, 'yyyy-MM-dd');

/** ¿La transacción ya dice lo que manda el plan? */
export const cuadra = (t: Transaccion, e: MovimientoEsperado) =>
  t.tipo === e.tipo
  && aCentimos(t.cantidad) === aCentimos(e.cantidad)
  && mismoDia(t.fecha, e.fecha)
  && (t.descripcion ?? '') === e.descripcion
  && !!t.adelanto === e.adelanto
  && (e.adelanto ? (t.persona ?? '') === (e.persona ?? '') : !t.persona);

/**
 * Compara lo esperado con las transacciones que ya generó este plan.
 * Si hubiera dos transacciones con el mismo origen (dos dispositivos a la vez),
 * la segunda se borra.
 */
export function diferencias(esperados: MovimientoEsperado[], existentes: Transaccion[]): DiferenciasSincronizacion {
  const porClave = new Map<string, Transaccion>();
  const borrar: Transaccion[] = [];
  existentes.forEach(t => {
    const clave = t.origenCompartido;
    if (!clave || porClave.has(clave)) borrar.push(t);
    else porClave.set(clave, t);
  });

  const crear: MovimientoEsperado[] = [];
  const actualizar: CambioTransaccion[] = [];
  const vistas = new Set<string>();
  esperados.forEach(e => {
    vistas.add(e.clave);
    const t = porClave.get(e.clave);
    if (!t) crear.push(e);
    else if (!cuadra(t, e)) actualizar.push({ transaccion: t, esperado: e });
  });
  porClave.forEach((t, clave) => { if (!vistas.has(clave)) borrar.push(t); });

  return { crear, actualizar, borrar };
}

export const hayDiferencias = (d: DiferenciasSincronizacion) =>
  d.crear.length + d.actualizar.length + d.borrar.length > 0;

/**
 * Separa lo que apuntaste tú (se pasa siempre solo) de lo que apuntaron otros
 * (se pasa solo si lo pides, o tras confirmarlo). Lo que se borra cuenta como
 * de quien apuntó el gasto o pago original.
 */
export function separarPorAutor(d: DiferenciasSincronizacion, uid: string) {
  const propias: DiferenciasSincronizacion = { crear: [], actualizar: [], borrar: [] };
  const ajenas: DiferenciasSincronizacion = { crear: [], actualizar: [], borrar: [] };
  d.crear.forEach(e => (e.creadoPor === uid ? propias : ajenas).crear.push(e));
  d.actualizar.forEach(c => (c.esperado.creadoPor === uid ? propias : ajenas).actualizar.push(c));
  d.borrar.forEach(t => ((t.compartidoCreadoPor ?? uid) === uid ? propias : ajenas).borrar.push(t));
  return { propias, ajenas };
}

/**
 * Cuántos gastos o pagos del plan hay detrás de unos cambios. Un gasto suele
 * dar dos transacciones (tu parte y el adelanto), pero para el usuario es uno.
 */
export function origenesAfectados(d: DiferenciasSincronizacion): number {
  const origen = (clave?: string) => (clave ?? '').split(':').slice(0, 2).join(':');
  return new Set([
    ...d.crear.map(e => origen(e.clave)),
    ...d.actualizar.map(c => origen(c.esperado.clave)),
    ...d.borrar.map(t => origen(t.origenCompartido) || t.id),
  ]).size;
}

/** Suma de cambios, para mostrar «N movimientos». */
export const totalCambios = (d: DiferenciasSincronizacion) =>
  d.crear.length + d.actualizar.length + d.borrar.length;
